/**
 * Connectors — the client contract for the "Apps & extensions" surface
 * (claude.ai "Get apps and extensions" parity).
 *
 * ── Why this is a contract, not a live integration ──────────────────────────
 * A backend audit (store-back) found exactly one integrations facility:
 * `IntegrationsDiagnosticsService` behind `GET /internal/diagnostics/integrations`.
 * That endpoint is **operator/deployment-level and internal-only** — it is gated
 * by `X-Internal-Api-Key`, steps the end-user JWT guard aside, and reports
 * env-config *presence* booleans + env-var handles (never a value, never a
 * per-user connection). There is **no per-user connect/disconnect/OAuth
 * connector API** a signed-in operator could call from the browser.
 *
 * So this module ships the *shape* of a real connectors backend (a typed
 * `ConnectorsApi` the surface talks to) plus an HONEST default implementation
 * that lists every catalogue entry as `coming_soon` and REFUSES to fabricate a
 * connection. The day store-back grows a user-facing connectors endpoint, only
 * `honestConnectorsApi` is swapped for an HTTP-backed `ConnectorsApi` — the
 * surface, the hook and the types are already correct.
 *
 * See `docs/connectors-gap.md` for the backend gap this documents.
 *
 * Hard rule mirrored from the backend: NEVER present a connection that does not
 * exist. No entry here is `connected` until a real backend says so.
 */

/** Stable connector identifiers. UI labels live in the i18n `settings` namespace. */
export type ConnectorId =
  | 'google_drive'
  | 'slack'
  | 'notion'
  | 'gmail'
  | 'google_calendar'
  | 'github'
  | 'gamma'
  | 'veeva'
  | 'sharepoint'
  | 'snowflake'
  | 'salesforce'
  | 'mcp';

/**
 * Lifecycle of a single connector.
 * - `coming_soon`  — no self-service backend yet (the only honest state today).
 * - `disconnected` — a backend exists and the user has not connected.
 * - `connected`    — a backend confirmed an active connection.
 * - `error`        — a backend reported a broken/failed connection.
 */
export type ConnectorStatus = 'coming_soon' | 'disconnected' | 'connected' | 'error';

/**
 * How the connector is reached. The claude.ai-parity surface only exposes web
 * apps today (the "Type" column reads "Web" for every catalogue entry); the union
 * leaves room for future transports without touching the table.
 */
export type ConnectorType = 'web';

/** Static, translation-agnostic catalogue entry (order + capability metadata). */
export interface Connector {
  id: ConnectorId;
  /**
   * True only once a real user-facing connect/disconnect backend exists for this
   * connector. Every entry is `false` today — see the module header.
   */
  available: boolean;
  /** Access type shown in the table's "Type" column. All `web` today. */
  type: ConnectorType;
  /** Highlighted in the "Populaire" discovery strip above the table. */
  popular: boolean;
}

/** Runtime state of a connector, as returned by a `ConnectorsApi`. */
export interface ConnectorState {
  id: ConnectorId;
  status: ConnectorStatus;
  /**
   * Optional secret-free detail (e.g. the connected account label). Never a
   * token or credential — mirrors the backend's secret-free contract.
   */
  detail?: string;
}

/**
 * The contract the Connectors surface talks to. A future HTTP client implements
 * this against a real store-back endpoint; today `honestConnectorsApi` does.
 */
export interface ConnectorsApi {
  /** Current state of every connector the deployment exposes. */
  list(): Promise<ConnectorState[]>;
  /** Begin/confirm a connection. Rejects when no backend can honour it. */
  connect(id: ConnectorId): Promise<ConnectorState>;
  /** Tear down a connection. Rejects when no backend can honour it. */
  disconnect(id: ConnectorId): Promise<ConnectorState>;
}

/**
 * The connector catalogue. These are integration *targets* the surface exposes:
 * popular self-service web apps (Google Drive, Slack, Notion, Gmail, Google
 * Calendar, GitHub, Gamma) alongside the enterprise/pharma targets aikka publicly
 * names (Veeva, SharePoint, Snowflake, Salesforce, plus a generic MCP entry).
 *
 * None is self-service yet, so each carries `available: false` and resolves to
 * `coming_soon`: the surface may *offer* a "Connecter" affordance (claude.ai
 * parity), but a click resolves to the backend's honest "provider not configured"
 * result — it never fabricates a live connection.
 *
 * `popular` marks the entries surfaced in the "Populaire" discovery strip; `type`
 * feeds the table's "Type" column. Catalogue order is the stable render order.
 *
 * No client, molecule, indication or proprietary reference appears here — only
 * third-party product names that are already public.
 */
export const CONNECTOR_CATALOGUE: readonly Connector[] = [
  { id: 'google_drive', available: false, type: 'web', popular: true },
  { id: 'slack', available: false, type: 'web', popular: true },
  { id: 'notion', available: false, type: 'web', popular: true },
  { id: 'gmail', available: false, type: 'web', popular: true },
  { id: 'google_calendar', available: false, type: 'web', popular: false },
  { id: 'github', available: false, type: 'web', popular: true },
  { id: 'gamma', available: false, type: 'web', popular: false },
  { id: 'veeva', available: false, type: 'web', popular: false },
  { id: 'sharepoint', available: false, type: 'web', popular: false },
  { id: 'snowflake', available: false, type: 'web', popular: false },
  { id: 'salesforce', available: false, type: 'web', popular: false },
  { id: 'mcp', available: false, type: 'web', popular: false },
] as const;

/** Thrown when a connector action is requested that no backend can fulfil. */
export class ConnectorUnavailableError extends Error {
  constructor(public readonly id: ConnectorId) {
    super(`Connector "${id}" has no self-service backend yet.`);
    this.name = 'ConnectorUnavailableError';
  }
}

/**
 * The honest default implementation: it reflects the catalogue as `coming_soon`
 * and refuses to invent a connection. This is what the app ships until a real
 * user-facing connectors endpoint exists.
 */
export const honestConnectorsApi: ConnectorsApi = {
  async list(): Promise<ConnectorState[]> {
    return CONNECTOR_CATALOGUE.map((c) => ({ id: c.id, status: 'coming_soon' as const }));
  },
  async connect(id: ConnectorId): Promise<ConnectorState> {
    // Never fabricate a connection.
    throw new ConnectorUnavailableError(id);
  },
  async disconnect(id: ConnectorId): Promise<ConnectorState> {
    throw new ConnectorUnavailableError(id);
  },
};
